import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, StickyNote, X } from 'lucide-react';
import { isBlank, sectionDomId, sectionProgress } from '../../services/assessmentSchema';

// Tracks which section is on screen, for highlighting the nav.
export function useActiveSection(sections) {
  const [active, setActive] = useState(sections[0]?.id);
  useEffect(() => {
    const els = sections.map((s) => document.getElementById(sectionDomId(s.id))).filter(Boolean);
    if (!els.length || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver((entries) => {
      const visible = entries.filter((e) => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible.length) setActive(visible[0].target.dataset.sectionId);
    }, { rootMargin: '-15% 0px -70% 0px' });
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [sections]);
  return [active, setActive];
}

function scrollParent(el) {
  for (let node = el.parentElement; node; node = node.parentElement) {
    const { overflowY } = window.getComputedStyle(node);
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) return node;
  }
  return document.scrollingElement || document.documentElement;
}

// Scrolls only the form's own scroll container. scrollIntoView would also
// scroll the page itself on phones (where 100vh is taller than the visible
// area), sliding the sticky section bar up under the fixed app header.
export function scrollToElement(el, { align = 'start', offset } = {}) {
  if (!el) return;
  // Clear the sticky section bar on small screens; on desktop there is none.
  const gap = offset ?? (window.innerWidth >= 1024 ? 16 : 84);
  const container = scrollParent(el);
  const elTop = el.getBoundingClientRect().top;
  const boxTop = container === document.scrollingElement ? 0 : container.getBoundingClientRect().top;
  let top = container.scrollTop + (elTop - boxTop);
  top -= align === 'center' ? Math.max(0, (container.clientHeight - el.offsetHeight) / 2) : gap;
  container.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
}

export function jumpToSection(id) {
  scrollToElement(document.getElementById(sectionDomId(id)));
}

function SectionLink({ section, values, active, onPick, memoUploaded }) {
  const p = sectionProgress(section, values);
  const usesFile = section.kind === 'memo' && memoUploaded;
  const hasNote = !isBlank(values[`note.${section.id}`]);
  return (
    <button
      type="button"
      onClick={() => onPick(section.id)}
      aria-current={active ? 'true' : undefined}
      className={`flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm transition-colors ${
        active ? 'bg-blue-50 text-blue-800 font-medium' : 'text-slate-700 hover:bg-slate-100'
      }`}
    >
      <span className="min-w-0 flex-1 truncate">{section.title}</span>
      {hasNote && <StickyNote className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-label="has notes" />}
      <span className={`shrink-0 text-xs tabular-nums ${p.missing && !usesFile ? 'text-amber-700' : 'text-slate-400'}`}>
        {usesFile ? 'File' : `${p.answered}/${p.total}`}
      </span>
    </button>
  );
}

// Desktop: a sticky list beside the form.
export function SectionSidebar({ sections, values, active, onPick, memoUploaded }) {
  return (
    <nav aria-label="Form sections" className="space-y-0.5">
      {sections.map((s) => (
        <SectionLink key={s.id} section={s} values={values} active={active === s.id} onPick={onPick} memoUploaded={memoUploaded} />
      ))}
    </nav>
  );
}

// Mobile: a sticky bar naming the current section; tapping it opens the list.
export function MobileSectionBar({ sections, values, active, onPick, memoUploaded }) {
  const [open, setOpen] = useState(false);
  const current = sections.find((s) => s.id === active) || sections[0];
  const pick = (id) => {
    setOpen(false);
    onPick(id);
  };
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white/95 px-3 py-2.5 text-left shadow-sm backdrop-blur"
      >
        <span className="min-w-0">
          <span className="block text-[11px] uppercase tracking-wide text-slate-500">Section</span>
          <span className="block truncate text-sm font-medium text-slate-900">{current?.title}</span>
        </span>
        <ChevronDown className="h-5 w-5 shrink-0 text-slate-500" />
      </button>
      {open && createPortal(
        <div className="fixed inset-0 z-40 flex" role="dialog" aria-modal="true" aria-label="Jump to section">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setOpen(false)} />
          <div className="relative ml-auto flex h-full w-[85%] max-w-sm flex-col bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <p className="text-sm font-semibold text-slate-900">Jump to section</p>
              <button type="button" onClick={() => setOpen(false)} className="rounded p-1 text-slate-500 hover:bg-slate-100" aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              <SectionSidebar sections={sections} values={values} active={active} onPick={pick} memoUploaded={memoUploaded} />
            </div>
          </div>
        </div>,
        // Portalled: the bar sits in a sticky (stacking-context) wrapper, which
        // would otherwise trap the drawer beneath the app header.
        document.body,
      )}
    </>
  );
}
